import { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Card,
  Switch,
  CircularProgress,
  Box,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Container
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "../api/api";

// Modern input styling matching the application's aesthetic
const modernInputStyle = {
  '& .MuiInputLabel-root': {
    color: '#A59D96',
    fontWeight: '600',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#D97706'
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#ECE5DA',
    },
    '&:hover fieldset': {
      borderColor: '#F29D17',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F8C332',
      borderWidth: '2px'
    }
  },
  '& .MuiOutlinedInput-input': {
    fontWeight: '700',
    color: '#2F2A25',
  }
};

export default function Menu() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // 👈 Loading state for global action dialogs
  const [isDialogActionLoading, setIsDialogActionLoading] = useState(false);

  const [selectedDrawerItem, setSelectedDrawerItem] = useState(null);

  // 👈 RESTORED: Form state that was accidentally removed
  const [form, setForm] = useState({
    name: "",
    category: "",
    description: "",
    image: null
  });

  // 🔥 STATE FOR THE CONFIRMATION DIALOG
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    isAlertOnly: false,
    confirmText: "CONFIRM"
  });

  const closeDialog = () => setDialogConfig((prev) => ({ ...prev, open: false }));

  const handleEdit = (item) => {
    setEditItem(item);

    setForm({
      name: item.name,
      category: item.category,
      description: item.description || "",
      image: null
    });

    setOpen(true);
  };

  const handleUpdate = async () => {
    setIsUpdating(true); 
    try {
      const formData = new FormData();

      formData.append("name", form.name);
      formData.append("category", form.category);
      formData.append("description", form.description);

      if (form.image) {
        formData.append("image", form.image);
      }

      const res = await api.put(
        `/vendor/menu-item/${editItem.item_id}`, 
        formData
      );

      if (res.data?.error) {
        setDialogConfig({
          open: true,
          title: "Update Failed",
          message: res.data.error,
          isAlertOnly: true
        });
        return;
      }

      setOpen(false);
      setForm({ name: "", category: "", description: "", image: null }); 
      
      setDialogConfig({
        open: true,
        title: "Success",
        message: "Menu item updated successfully!",
        isAlertOnly: true
      });
      fetchMenu();

    } catch (err) {
      console.log(err);
      setDialogConfig({
        open: true,
        title: "Network Error",
        message: "Network error while updating.",
        isAlertOnly: true
      });
    } finally {
      setIsUpdating(false); 
    }
  };

  const vendorId = localStorage.getItem("vendor_id");

  const fetchVendor = useCallback(async () => {
    try {
      const res = await api.get(`/vendor/${vendorId}`);
      setIsOpen(res.data.is_open);
    } catch (err) {
      console.log("Vendor fetch error:", err);
    }
  }, [vendorId]);

  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      const timestamp = new Date().getTime();
      const res = await api.get(`/vendor/${vendorId}/menu/vendor-view?t=${timestamp}`);
      
      const dataWithFreshImages = res.data.map(item => ({
        ...item,
        image_url: item.image_url ? `${item.image_url}?v=${timestamp}` : null
      }));

      setMenu(dataWithFreshImages);
    } catch (err) {
      console.log("ERROR:", err);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) {
      fetchMenu();
      fetchVendor();
    }
  }, [vendorId, fetchMenu, fetchVendor]);

  // =========================
  // KITCHEN TOGGLE WITH CONFIRMATION
  // =========================
  const handleKitchenToggleClick = () => {
    setDialogConfig({
      open: true,
      title: "Kitchen Status",
      message: `Are you sure you want to ${isOpen ? "close" : "open"} your kitchen?`,
      confirmText: isOpen ? "CLOSE KITCHEN" : "OPEN KITCHEN",
      isAlertOnly: false,
      onConfirm: toggleKitchen
    });
  };

  const toggleKitchen = async () => {
    setIsDialogActionLoading(true); // 👈 Show spinner in dialog
    try {
      const res = await api.put(`/vendor/${vendorId}/toggle-status`);
      setIsOpen(res.data.is_open);
      closeDialog(); // 👈 Close dialog AFTER completion
    } catch (err) {
      console.log("Kitchen toggle error:", err);
      // Overwrite dialog state with error instead of closing
      setDialogConfig({
        open: true,
        title: "Error",
        message: "Failed to update kitchen status. Please try again.",
        isAlertOnly: true
      });
    } finally {
      setIsDialogActionLoading(false); // 👈 Hide spinner
    }
  };

  // =========================
  // ITEM TOGGLE WITH CONFIRMATION
  // =========================
  const handleToggleItemClick = (menuId, e) => {
    if (e) e.stopPropagation();

    if (!isOpen) {
      setDialogConfig({
        open: true,
        title: "Kitchen Closed",
        message: "You must open the kitchen before updating menu items.",
        isAlertOnly: true
      });
      return; 
    }

    const itemBeingToggled = menu.find(i => i.menu_id === menuId) || (selectedDrawerItem?.menu_id === menuId ? selectedDrawerItem : null);
    if (!itemBeingToggled) return;

    setDialogConfig({
      open: true,
      title: "Change Availability",
      message: `Are you sure you want to mark ${itemBeingToggled.name} as ${itemBeingToggled.is_available ? "Unavailable" : "Available"}?`,
      confirmText: "UPDATE",
      isAlertOnly: false,
      onConfirm: () => toggleItem(menuId),
      buttonColor: itemBeingToggled.is_available ? "#E85D3F" : "#577A34",
      hoverColor: itemBeingToggled.is_available ? "#D64D31" : "#4A692A"
    });
  };

  const toggleItem = async (menuId) => {
    setIsDialogActionLoading(true); // 👈 Show spinner in dialog

    const previousMenu = [...menu];
    const previousDrawerItem = selectedDrawerItem ? { ...selectedDrawerItem } : null;

    try {
      // Optimistic UI state update while background call happens
      setMenu((prev) =>
        prev.map((item) =>
          item.menu_id === menuId
            ? { ...item, is_available: !item.is_available }
            : item
        )
      );

      if (selectedDrawerItem && selectedDrawerItem.menu_id === menuId) {
        setSelectedDrawerItem((prev) => ({ ...prev, is_available: !prev.is_available }));
      }

      await api.put(`/vendor/menu/${menuId}`);
      closeDialog(); // 👈 Close dialog AFTER completion

    } catch (err) {
      console.log("Toggle error:", err);
      
      // Revert states
      setMenu(previousMenu);
      if (previousDrawerItem) setSelectedDrawerItem(previousDrawerItem);
      
      // Show error dialog
      setDialogConfig({
        open: true,
        title: "Error",
        message: "Failed to update availability. Please check your connection.",
        isAlertOnly: true
      });
    } finally {
      setIsDialogActionLoading(false); // 👈 Hide spinner
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pt: { xs: 2, sm: 3 }, pb: 10, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="lg" disableGutters>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Today's Menu
          </Typography>
        </Box>

        {/* 🔥 Kitchen Toggle Area */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          mb={4} 
          p={{ xs: 2, sm: 3 }} 
          sx={{ 
            bgcolor: "#FFFFFF", 
            borderRadius: "24px", 
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)", 
            border: "none",
            transition: "transform 0.2s ease",
            "&:hover": { transform: "translateY(-2px)" }
          }}
        >
          <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>
            KITCHEN STATUS
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="subtitle2" fontWeight="900" sx={{ color: isOpen ? "#577A34" : "#E85D3F" }}>
              {isOpen ? "OPEN" : "CLOSED"}
            </Typography>
            <Switch checked={isOpen} onChange={handleKitchenToggleClick} color="success" sx={{ transform: 'scale(1.1)' }} />
          </Box>
        </Box>

        {/* 🔄 Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress size={48} thickness={4} sx={{ color: '#F8C332' }} />
          </Box>
        )}

        {/* ❌ Empty */}
        {!loading && menu.length === 0 && (
          <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#FFFFFF', borderRadius: '24px', border: '1px dashed #ECE5DA' }}>
            <Typography color="#A59D96" fontWeight="700">No menu items assigned to your profile yet.</Typography>
          </Box>
        )}

        {/* ✅ Menu List */}
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }, 
            gap: { xs: 2, sm: 3 }
          }}
        >
          {menu.map((item) => (
            <Card 
              key={item.menu_id}
              onClick={() => setSelectedDrawerItem(item)}
              elevation={0}
              sx={{ 
                borderRadius: "24px", 
                p: 1.5, 
                textAlign: 'left', 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                minWidth: 0, 
                overflow: 'hidden',
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
                border: "none",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                opacity: item.is_available ? 1 : 0.75,
                bgcolor: item.is_available ? "#FFFFFF" : "#FAFAFA",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 16px 32px rgba(0, 0, 0, 0.12)",
                }
              }}
            >
              {item.image_url ? (
                <CardMedia
                  component="img"
                  image={item.image_url}
                  alt={item.name}
                  sx={{ 
                    height: { xs: 120, sm: 140, md: 160 }, 
                    borderRadius: "16px", 
                    objectFit: "cover", 
                    width: '100%',
                    filter: (isOpen && item.is_available) ? 'none' : 'grayscale(100%)', 
                    transition: 'filter 0.3s ease'
                  }}
                />
              ) : (
                <Box sx={{ height: { xs: 120, sm: 140, md: 160 }, borderRadius: "16px", bgcolor: "#FFFDF8", border: '1px dashed #ECE5DA', display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography fontSize={12} fontWeight="700" color="#A59D96">No Image</Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  sx={{
                    minWidth: 14, height: 14, mt: 0.3,
                    border: `1.5px solid ${item.category?.toLowerCase().includes('non') ? '#E85D3F' : '#577A34'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px'
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.category?.toLowerCase().includes('non') ? '#E85D3F' : '#577A34' }} />
                </Box>
                <Typography variant="body2" fontWeight="900" sx={{ lineHeight: 1.2, color: '#3A2C23' }}>
                  {item.name}
                </Typography>
              </Box>

              <Typography variant="subtitle1" fontWeight="900" sx={{ color: "#3A2C23", mt: 1, mb: 0.5 }}>
                ₹{item.vendor_price}
              </Typography>

              <Box 
                sx={{ 
                  mt: 'auto', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  width: '100%',
                  pt: 1.5
                }}
              >
                <Button
                  disableElevation
                  onClick={(e) => handleToggleItemClick(item.menu_id, e)} 
                  disabled={!isOpen}
                  sx={{
                    borderRadius: "12px",
                    py: 0.5,
                    px: 1.5,
                    minWidth: 0,
                    textTransform: "none",
                    fontWeight: "900",
                    fontSize: "0.7rem",
                    bgcolor: item.is_available ? "#E1EED3" : "#FFF0EE",
                    color: item.is_available ? "#577A34" : "#E85D3F",
                    border: "none",
                    transition: "0.2s",
                    '&:hover': {
                      bgcolor: item.is_available ? "#DDECC8" : "#FFE4E0",
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: item.is_available ? "#577A34" : "#E85D3F",
                      mr: 1.5
                    }}
                  />
                  {item.is_available ? "Available" : "Unavailable"}
                </Button>

                <Typography
                  variant="caption"
                  sx={{ 
                    color: "#A59D96", 
                    cursor: "pointer", 
                    fontWeight: "800", 
                    fontSize: '0.7rem', 
                    letterSpacing: 0.5,
                    py: 0.5,
                    px: 0.5,
                    '&:hover': { color: "#3A2C23" }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item);
                  }}
                >
                  EDIT
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>

        {/* ================= DRAWER (BOTTOM SHEET) ================= */}
        <Drawer
          anchor="bottom"
          open={!!selectedDrawerItem}
          onClose={() => setSelectedDrawerItem(null)}
          PaperProps={{
            sx: {
              borderRadius: "32px 32px 0 0",
              overflow: "visible",
              height: "75vh",
              maxHeight: "75vh",
              display: "flex",
              flexDirection: "column",
              maxWidth: "600px", 
              margin: "0 auto", 
              width: "100%",
              bgcolor: '#FFFDF8',
              boxShadow: "0 -10px 40px rgba(0,0,0,0.15)"
            },
          }}
        >
          {selectedDrawerItem && (
            <>
              <IconButton
                onClick={() => setSelectedDrawerItem(null)}
                sx={{
                  position: "absolute",
                  top: -60,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "#FFFFFF",
                  color: "#3A2C23",
                  zIndex: 1300,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  "&:hover": { bgcolor: "#FFFDF8" },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box sx={{ overflowY: "auto", flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Box sx={{ position: "relative", mb: 3 }}>
                  {selectedDrawerItem.image_url ? (
                    <CardMedia
                      component="img"
                      image={selectedDrawerItem.image_url}
                      sx={{
                        height: { xs: 220, sm: 280, md: 320 }, 
                        borderRadius: "24px",
                        objectFit: "cover",
                        filter: (isOpen && selectedDrawerItem.is_available) ? "none" : "grayscale(100%)",
                        transition: 'filter 0.3s ease',
                        boxShadow: "0 10px 24px rgba(0,0,0,0.08)"
                      }}
                    />
                  ) : (
                    <Box sx={{ height: { xs: 220, sm: 280, md: 320 }, borderRadius: "24px", bgcolor: "#FFFFFF", border: '1px dashed #ECE5DA', display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography color="#A59D96" fontWeight="700">No Image Uploaded</Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ px: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 16, height: 16,
                        border: `2px solid ${selectedDrawerItem.category?.toLowerCase().includes("non") ? "#E85D3F" : "#577A34"}`,
                        display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px",
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: selectedDrawerItem.category?.toLowerCase().includes("non") ? "#E85D3F" : "#577A34" }} />
                    </Box>
                    <Typography variant="h5" fontWeight="900" sx={{ color: '#3A2C23', lineHeight: 1.2 }}>
                      {selectedDrawerItem.name}
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: "#3A2C23", fontWeight: 900, fontSize: '1.4rem', mb: 2 }}>
                    ₹{selectedDrawerItem.vendor_price}
                  </Typography>

                  <Typography variant="body1" sx={{ color: "#6E655E", lineHeight: 1.6, fontWeight: '600' }}>
                    {selectedDrawerItem.description || "No description provided for this item."}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderTop: "1px dashed #ECE5DA",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "#FFFFFF",
                }}
              >
                <Button
                  fullWidth
                  disableElevation
                  onClick={() => handleToggleItemClick(selectedDrawerItem.menu_id)} 
                  disabled={!isOpen}
                  sx={{
                    borderRadius: "16px",
                    py: 1.5,
                    fontWeight: "900",
                    fontSize: "0.95rem",
                    textTransform: "none",
                    bgcolor: selectedDrawerItem.is_available ? "#E1EED3" : "#FFF0EE",
                    color: selectedDrawerItem.is_available ? "#577A34" : "#E85D3F",
                    "&:hover": {
                      bgcolor: selectedDrawerItem.is_available ? "#DDECC8" : "#FFE4E0",
                    }
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: selectedDrawerItem.is_available ? "#577A34" : "#E85D3F", mr: 1.5 }} />
                  {selectedDrawerItem.is_available ? "Available" : "Unavailable"}
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={() => {
                    handleEdit(selectedDrawerItem);
                    setSelectedDrawerItem(null); 
                  }}
                  sx={{
                    bgcolor: "#F8C332",
                    color: "#3A2C23",
                    borderRadius: "16px",
                    py: 1.5,
                    fontWeight: "900",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    boxShadow: "0 4px 12px rgba(248, 195, 50, 0.3)",
                    "&:hover": { bgcolor: "#F5B71D" },
                  }}
                >
                  Edit Details
                </Button>
              </Box>
            </>
          )}
        </Drawer>
        
        {/* ================= MODERNIZED EDIT FORM DIALOG ================= */}
        <Dialog 
          open={open} 
          onClose={() => setOpen(false)} 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "24px",
              p: 1,
              bgcolor: '#FFFDF8',
              boxShadow: "0px 12px 40px rgba(0,0,0,0.12)"
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: "900", color: '#3A2C23', textAlign: "center", fontSize: "1.4rem", pt: 3, pb: 1 }}>
            Edit Menu Item
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 4 } }}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={modernInputStyle} 
            />
            <TextField
              select
              label="Category"
              fullWidth
              margin="normal"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              sx={modernInputStyle}
            >
              <MenuItem value="Veg" sx={{ fontWeight: "700", color: '#3A2C23' }}>Veg</MenuItem>
              <Divider />
              <MenuItem value="Non Veg" sx={{ fontWeight: "700", color: '#3A2C23' }}>Non Veg</MenuItem>
            </TextField>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              margin="normal"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              sx={modernInputStyle}
            />

            <Box mt={3}>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  border: "2px dashed #ECE5DA",
                  borderRadius: "16px",
                  bgcolor: "#FFFFFF",
                  cursor: "pointer",
                  transition: "0.2s ease",
                  "&:hover": { borderColor: "#F8C332", bgcolor: "#FFF6DE" }
                }}
              >
                <input
                  key={editItem?.item_id || "file-input"}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    setForm({ ...form, image: e.target.files[0] })
                  }
                />
                <Typography variant="body2" fontWeight="800" color="#6E655E">
                  Tap to upload new image
                </Typography>
              </Box>
            </Box>

            <Box display="flex" justifyContent="center" mt={3}>
              {form.image ? (
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: '#F8C332', fontWeight: '800', letterSpacing: 0.5 }}>NEW IMAGE PREVIEW</Typography>
                  <br />
                  <img
                    src={URL.createObjectURL(form.image)}
                    alt="preview"
                    width={140}
                    style={{ marginTop: 8, borderRadius: "16px", border: "2px solid #F8C332" }}
                  />
                </Box>
              ) : editItem?.image_url ? (
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>CURRENT IMAGE</Typography>
                  <br />
                  <img
                    src={editItem.image_url}
                    alt="current"
                    width={140}
                    style={{ marginTop: 8, borderRadius: "16px", opacity: 0.9 }}
                  />
                </Box>
              ) : null}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: { xs: 2, sm: 4 }, pb: 3, pt: 2, justifyContent: "space-between", gap: 2 }}>
            <Button 
              onClick={() => setOpen(false)} 
              disabled={isUpdating}
              sx={{ fontWeight: "800", color: "#6E655E", borderRadius: "12px", px: 3, py: 1.2, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleUpdate}
              disabled={!editItem || isUpdating} 
              sx={{ 
                fontWeight: "900", 
                borderRadius: "12px", 
                px: 4,
                py: 1.2,
                bgcolor: "#F8C332",
                color: "#3A2C23",
                textTransform: 'none',
                minHeight: '42px', // Prevent resize during load
                boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
                "&:hover": { bgcolor: "#F5B71D" },
                "&.Mui-disabled": { bgcolor: "#fbe49c", color: "#3A2C23" }
              }}
            >
              {isUpdating ? <CircularProgress size={24} sx={{ color: '#3A2C23' }} /> : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= GLOBAL CONFIRMATION / ALERT DIALOG ================= */}
        <Dialog
          open={dialogConfig.open}
          // 👈 Prevent dialog close during loading
          onClose={isDialogActionLoading ? undefined : closeDialog}
          PaperProps={{
            sx: {
              borderRadius: "24px",
              p: 1,
              bgcolor: '#FFFFFF',
              minWidth: { xs: "300px", sm: "360px" },
              boxShadow: "0px 16px 48px rgba(0,0,0,0.15)",
              textAlign: "center"
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: "900", color: '#3A2C23', fontSize: "1.3rem", pb: 1, pt: 3 }}>
            {dialogConfig.title}
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Typography sx={{ color: "#6E655E", fontWeight: "600", fontSize: "0.95rem", lineHeight: 1.5 }}>
              {dialogConfig.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: "flex", gap: 1.5 }}>
            {dialogConfig.isAlertOnly ? (
              <Button
                onClick={closeDialog}
                variant="contained"
                disableElevation
                fullWidth
                sx={{ borderRadius: "12px", py: 1.2, minHeight: "52px", fontWeight: "900", bgcolor: "#F8C332", color: '#3A2C23', "&:hover": { bgcolor: "#F5B71D" } }}
              >
                Okay
              </Button>
            ) : (
              <>
                <Button
                  fullWidth
                  onClick={closeDialog}
                  variant="outlined"
                  disabled={isDialogActionLoading} // 👈 Disable while loading
                  sx={{ 
                    flex: 1, 
                    minHeight: "52px",
                    borderRadius: "12px", 
                    py: 1.2, 
                    fontWeight: "800", 
                    color: "#6E655E", 
                    borderColor: "#ECE5DA", 
                    textTransform: 'none'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  onClick={dialogConfig.onConfirm}
                  variant="contained"
                  disableElevation
                  disabled={isDialogActionLoading} // 👈 Disable while loading
                  sx={{ 
                    flex: 1,
                    minHeight: "52px", // Prevent resize
                    borderRadius: "12px", 
                    py: 1.2, 
                    fontWeight: "900", 
                    textTransform: 'none', 
                    lineHeight: 1.2,
                    bgcolor: dialogConfig.buttonColor || (dialogConfig.confirmText.includes("CLOSE") || dialogConfig.confirmText.includes("DELETE") ? "#E85D3F" : "#577A34"), 
                    color: "#FFFFFF",
                    "&:hover": { 
                      bgcolor: dialogConfig.hoverColor || (dialogConfig.confirmText.includes("CLOSE") || dialogConfig.confirmText.includes("DELETE") ? "#D64D31" : "#4A692A") 
                    },
                    "&.Mui-disabled": {
                      bgcolor: dialogConfig.buttonColor ? `${dialogConfig.buttonColor}90` : "#ccc",
                      color: "#fff"
                    }
                  }}
                >
                  {/* 👈 Render proper loading spinner */}
                  {isDialogActionLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : dialogConfig.confirmText}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}